import { getOriginOfAssetsOptions } from '@/constants/formOptions';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import type { FormAData, FormAProps } from '../types/formA.types';
import { createFormASchema } from '../types/formA.types';
import {
  createEmptyBeneficialOwner,
  getFormADefaultValues,
  shouldShowBeneficialOwnersSection,
  shouldShowOriginOfAssetsOther,
} from '../utils/formA.utils';

/**
 * Custom hook for Form A logic and state management
 * Encapsulates all form state, effects, and handlers
 */
export const useFormA = (props: FormAProps) => {
  const { onSubmit, onError, onChange, defaultValues } = props;

  // Translation hook
  const { t: tForms } = useFormsTranslation();

  // Track if this is the initial mount to avoid unnecessary resets
  const isInitialMount = useRef(true);

  // Create schema with translations
  const formASchema = useMemo(() => createFormASchema(tForms), [tForms]);

  // Submit error state
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Initialize form with react-hook-form
  const form = useForm<FormAData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(formASchema) as any,
    mode: 'all', // Validate on all events (blur, change, submit, trigger)
    defaultValues: getFormADefaultValues(defaultValues),
  });

  /**
   * Effect to reset form when defaultValues changes (e.g., when form is hidden and data is cleared).
   * This ensures the form state is properly cleaned up when the tab becomes hidden and then visible again.
   */
  useEffect(() => {
    // Skip the initial mount - defaultValues are already applied
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Check if form data is being cleared (all key fields are empty)
    const isDataCleared =
      (!defaultValues?.beneficialOwnerType ||
        defaultValues.beneficialOwnerType === '') &&
      (!defaultValues?.beneficialOwners ||
        defaultValues.beneficialOwners.length === 0) &&
      (!defaultValues?.originOfAssets ||
        defaultValues.originOfAssets.length === 0);

    if (isDataCleared) {
      // Reset the form to default empty values
      form.reset(getFormADefaultValues());
    }
  }, [defaultValues, form]);

  // Field array for beneficial owners
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'beneficialOwners',
  });

  // Watch form values
  const watchOriginOfAssets = useWatch({
    control: form.control,
    name: 'originOfAssets',
  });
  const watchBeneficialOwnerType = useWatch({
    control: form.control,
    name: 'beneficialOwnerType',
  });

  // Memoized origin of assets options
  const originOfAssetsOptions = useMemo(
    () => getOriginOfAssetsOptions(tForms),
    [tForms]
  );

  // Computed values
  const showBeneficialOwnersSection = shouldShowBeneficialOwnersSection(
    watchBeneficialOwnerType
  );
  const showOriginOfAssetsOtherField =
    shouldShowOriginOfAssetsOther(watchOriginOfAssets);

  /**
   * Automatically add a beneficial owner when "others" or "sole" is selected
   */
  useEffect(() => {
    if (
      (watchBeneficialOwnerType === 'sole' ||
        watchBeneficialOwnerType === 'others') &&
      fields.length === 0
    ) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      append(createEmptyBeneficialOwner() as any);
    }
  }, [watchBeneficialOwnerType, fields.length, append]);

  /**
   * Clear beneficial owners when switching away from 'sole' or 'others'
   */
  useEffect(() => {
    if (
      watchBeneficialOwnerType !== 'sole' &&
      watchBeneficialOwnerType !== 'others' &&
      fields.length > 0
    ) {
      // Remove all beneficial owners when not needed
      for (let i = fields.length - 1; i >= 0; i--) {
        remove(i);
      }
    }
  }, [watchBeneficialOwnerType, fields.length, remove]);

  /**
   * Clear origin of assets other text field when "other" is not selected
   */
  useEffect(() => {
    if (
      !showOriginOfAssetsOtherField &&
      form.getValues('originOfAssetsOther')
    ) {
      form.setValue('originOfAssetsOther', '');
    }
  }, [showOriginOfAssetsOtherField, form]);

  /**
   * Memoized onChange handler
   */
  const handleFormChange = useCallback(
    (data: Partial<FormAData>) => {
      if (onChange) {
        onChange(data);
      }
    },
    [onChange]
  );

  /**
   * Debounced onChange effect using form.watch() subscription
   * This approach eliminates the re-render caused by useWatch on every keystroke
   */
  useEffect(() => {
    if (!onChange) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const subscription = form.watch(data => {
      // Clear any pending debounce
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Debounce the onChange callback
      timeoutId = setTimeout(() => {
        handleFormChange(data as Partial<FormAData>);
      }, 300); // 300ms debounce for better performance
    });

    return () => {
      subscription.unsubscribe();
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [form, handleFormChange, onChange]);

  /**
   * Form submission handler
   */
  const handleSubmit = async (data: FormAData) => {
    setSubmitError(null);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      onSubmit?.(data);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An error occurred';
      setSubmitError(errorMessage);
      onError?.(error as Error);
    }
  };

  /**
   * Handler to add a beneficial owner
   */
  const handleAddBeneficialOwner = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    append(createEmptyBeneficialOwner() as any);
  };

  /**
   * Handler to remove a beneficial owner by index
   */
  const handleRemoveBeneficialOwner = (index: number) => {
    remove(index);
  };

  /**
   * Trigger form validation programmatically
   */
  const triggerValidation = async () => {
    const isValid = await form.trigger();
    return isValid;
  };

  return {
    // Form instance
    form,
    // Field array
    fields,
    // Handlers
    handleSubmit,
    handleAddBeneficialOwner,
    handleRemoveBeneficialOwner,
    triggerValidation,
    // State
    submitError,
    // Computed values
    showBeneficialOwnersSection,
    showOriginOfAssetsOtherField,
    originOfAssetsOptions,
    // Translation
    tForms,
  };
};
